import { m } from "@recobra/i18n/messages";
import type { RequestedDocumentInput } from "@recobra/schemas/document";
import {
  type CreateTaskInput,
  DvcSearchDetailsSchema,
  type DvcSearchType,
  isDvcSearchTypeAvailable,
  type Task,
  type TaskPriority,
  type UpdateTaskInput,
} from "@recobra/schemas/task";
import { type SystemTaskKey, systemTaskType } from "@recobra/schemas/task-catalog";

import { getErrorMessage } from "../../lib/form";

export interface TaskFormValues {
  taskType: string;
  description: string;
  priority: TaskPriority;
  assignedTo: string;
  deadlineDate: string;
  requestedDocuments: RequestedDocumentInput[];
  dvcSearchType: DvcSearchType | "";
  dvcSearchData: string;
}

export const taskFormDefaultValues: TaskFormValues = {
  taskType: "",
  description: "",
  priority: "medium",
  assignedTo: "",
  deadlineDate: "",
  requestedDocuments: [],
  dvcSearchType: "",
  dvcSearchData: "",
};

export const taskToFormValues = (task: Task): TaskFormValues => ({
  taskType: task.type,
  description: task.description ?? "",
  priority: task.priority ?? "medium",
  assignedTo: task.assignedTo ?? "",
  deadlineDate: task.deadlineDate ?? "",
  requestedDocuments: task.requestedDocuments ?? [],
  dvcSearchType: task.dvcSearchDetails?.type ?? "",
  dvcSearchData: task.dvcSearchDetails?.dataToSearch ?? "",
});

/** Only types that collect a document list may send one; the API rejects the rest. */
export const collectsRequestedDocuments = (system: SystemTaskKey | null): boolean =>
  systemTaskType(system)?.collects?.requestedDocuments === true;

/** Only types that collect a DVC search may send one; the API rejects the rest. */
export const collectsDvcSearchDetails = (system: SystemTaskKey | null): boolean =>
  systemTaskType(system)?.collects?.dvcSearchDetails === true;

/**
 * A DVC task needs both a search type and a value matching that type's format,
 * and the search has to be one the case's own age still allows.
 */
export const dvcSearchDetailsErrors = (
  value: Pick<TaskFormValues, "dvcSearchType" | "dvcSearchData">,
  system: SystemTaskKey | null,
  incidentDate?: string | null,
): { dvcSearchType?: string; dvcSearchData?: string } | undefined => {
  if (!collectsDvcSearchDetails(system)) return undefined;

  if (!value.dvcSearchType || !value.dvcSearchData.trim()) {
    const message = m.task_dvc_search_details_required();

    return {
      ...(value.dvcSearchType ? {} : { dvcSearchType: message }),
      ...(value.dvcSearchData.trim() ? {} : { dvcSearchData: message }),
    };
  }

  if (!isDvcSearchTypeAvailable(value.dvcSearchType, incidentDate)) {
    return { dvcSearchType: m.task_dvc_search_type_unavailable() };
  }

  const result = DvcSearchDetailsSchema.safeParse({
    type: value.dvcSearchType,
    dataToSearch: value.dvcSearchData.trim(),
  });

  return result.success
    ? undefined
    : { dvcSearchData: getErrorMessage(result.error.issues[0]?.message) };
};

/** The form holds a blank detail as "", which the API's optional label rejects. */
const requestedDocumentsPayload = (value: TaskFormValues): RequestedDocumentInput[] =>
  value.requestedDocuments.map(({ label, ...entry }) => ({
    ...entry,
    ...(label?.trim() ? { label: label.trim() } : {}),
  }));

const dvcSearchDetailsPayload = (value: TaskFormValues): CreateTaskInput["dvcSearchDetails"] =>
  value.dvcSearchType && value.dvcSearchData.trim()
    ? { type: value.dvcSearchType, dataToSearch: value.dvcSearchData.trim() }
    : undefined;

export const buildCreateTaskInput = (
  value: TaskFormValues,
  taskType: string,
  system: SystemTaskKey | null,
  context: {
    contextType: "case" | "client";
    contextId: string;
    contextStage?: string;
  },
): CreateTaskInput => ({
  type: taskType,
  description: value.description || undefined,
  priority: value.priority,
  assignedTo: value.assignedTo || undefined,
  contextType: context.contextType,
  contextId: context.contextId,
  contextStage: context.contextStage || undefined,
  ...(collectsRequestedDocuments(system)
    ? { requestedDocuments: requestedDocumentsPayload(value) }
    : {}),
  ...(collectsDvcSearchDetails(system) ? { dvcSearchDetails: dvcSearchDetailsPayload(value) } : {}),
});

export const buildTaskFieldUpdate = (
  value: TaskFormValues,
  field: "assignee" | "deadlineDate" | "requestedDocuments" | "dvcSearchDetails",
): UpdateTaskInput => {
  if (field === "deadlineDate") return { deadlineDate: value.deadlineDate };
  if (field === "assignee") return { assignedTo: value.assignedTo || null };
  if (field === "requestedDocuments") {
    return { requestedDocuments: requestedDocumentsPayload(value) };
  }

  return { dvcSearchDetails: dvcSearchDetailsPayload(value) };
};
